import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import {
    LIST_FILTER_DATE_FIELD_SX,
    LIST_FILTER_DIVIDER_SX,
    LIST_FILTER_SELECT_FIELD_SX,
    ListFilterBar,
    ListFilterForm,
} from '../../component/ListFilterLayout'
import { formatSafeNumber } from '../../utils/numberDisplay'
import OnboardingCountryFilterEditor from './OnboardingCountryFilterEditor'
import {
    ONBOARDING_COUNTRY_CODES,
    readOnboardingCountryPreferences,
    saveOnboardingCountryPreferences,
} from './onboardingCountryPreferences'
import OnboardingPeriodAverage, {
    ENTRY_AVERAGE_METRICS,
    COHORT_AVERAGE_METRICS,
    STEP_AVERAGE_METRICS,
} from './OnboardingPeriodAverage'
import {
    createInitialOnboardingRange,
    createOnboardingPresetRange,
    getCombinedEntryRows,
    flattenStepRows,
    formatCountry,
    formatEntryFrequency,
    formatHighestDropKind,
    formatHighestDropStep,
    formatOnboardingFlow,
    formatOptionalCount,
    formatQualityWarning,
    formatRate,
    formatRefreshedAt,
    formatSnsMethod,
    getFlowComparisonBarWidth,
    getFlowComparisonsForRange,
    getCountryComparisonsForRange,
    getHighestDropByDate,
    getIndividualKpiForRange,
    getMemberTypeForRange,
    getSnsAuthForRange,
    getStepCohortForRange,
    sortOnboardingRowsNewestFirst,
    validateOnboardingRange,
} from './onboardingFunnelUtils'
import {
    getOnboardingFunnelSteps,
    getOnboardingFunnelSummary,
} from './onboardingFunnelApi'

export { ONBOARDING_FUNNEL_SUMMARY_PATH } from './onboardingFunnelApi'

const DATA_STATUS_CONFIG = {
    provisional: { label: '잠정', color: 'warning' },
    final: { label: '확정', color: 'success' },
}

const HIGHEST_DROP_CELL_SX = {
    fontWeight: 700,
    color: 'warning.dark',
    bgcolor: 'warning.light',
}

const PLATFORM_OPTIONS = [
    { value: 'ALL', label: '전체 OS' },
    { value: 'IOS', label: 'iOS' },
    { value: 'ANDROID', label: 'Android' },
]

const RANGE_PRESET_OPTIONS = [
    { value: '7', label: '최근 7일' },
    { value: '28', label: '최근 28일' },
    { value: '90', label: '최근 90일' },
    { value: 'CUSTOM', label: '직접 지정' },
]

const FILTER_AUTO_APPLY_DELAY_MS = 300

const MEMBER_TYPE_METRICS = [
    { key: 'signupStartUsers', label: '가입 시작 사용자', color: 'text.primary' },
    { key: 'individualSelectedUsers', label: '일반회원 선택', color: 'primary.main' },
    { key: 'companySelectedUsers', label: '기업회원 선택', color: 'secondary.main' },
    { key: 'unselectedUsers', label: '유형 미선택', color: 'warning.dark' },
    { key: 'selectionRate', label: '유형 선택률', color: 'info.main', rate: true },
]

const SNS_MARKET_SECTIONS = [
    {
        marketType: 'domestic',
        label: '국내',
        note: '카카오 · 네이버 · 구글 · 애플',
    },
    {
        marketType: 'global',
        label: '해외',
        note: '구글 · 애플만 지원 — 카카오 · 네이버 미제공',
    },
]

const STEP_FLOW_OPTIONS = [
    {
        value: 'domestic_local',
        label: '국내 · 자체계정',
        marketType: 'domestic',
        authType: 'local',
        method: 'email',
    },
    {
        value: 'domestic_sns',
        label: '국내 · SNS',
        marketType: 'domestic',
        authType: 'sns',
        method: 'ALL',
    },
    {
        value: 'global_local',
        label: '해외 · 자체계정',
        marketType: 'global',
        authType: 'local',
        method: 'email',
    },
    {
        value: 'global_sns',
        label: '해외 · SNS',
        marketType: 'global',
        authType: 'sns',
        method: 'ALL',
    },
]

const createEmptySummary = () => ({
    meta: {
        availableFrom: null,
        dataThrough: null,
        unavailableDates: [],
        refreshedAt: null,
    },
    days: [],
})

const createEmptyStepSummary = () => ({
    meta: {
        dataThrough: null,
        unavailableDates: [],
    },
    filter: null,
    days: [],
})

const getStepFlow = flowValue => (
    STEP_FLOW_OPTIONS.find(option => option.value === flowValue)
    || STEP_FLOW_OPTIONS[0]
)

const createUnavailableDescription = (details) => {
    const availableFrom = details?.availableFrom || '-'
    const dataThrough = details?.dataThrough || '-'

    return `현재 제공 가능 범위: ${availableFrom} ~ ${dataThrough}`
}

function MetadataItem({ label, value }) {
    return (
        <Box sx={{ minWidth: 150, px: 1.5, py: 1.25 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700 }}>{value || '-'}</Typography>
        </Box>
    )
}

function DailyTableToggle({ id, label, expanded, onClick }) {
    return (
        <Button
            onClick={onClick}
            aria-expanded={expanded}
            aria-controls={id}
            aria-label={`${label} ${expanded ? '접기' : '펼치기'}`}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />}
            sx={{ alignSelf: 'flex-start', mb: 1 }}
        >
            {label} {expanded ? '접기' : '펼치기'}
        </Button>
    )
}

function EntryMetricValue({ row, field, rate = false }) {
    const format = rate ? formatRate : formatOptionalCount
    return (
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {format(row[field])}
            <Box component="span" sx={{ ml: 0.75, color: 'text.secondary', fontWeight: 400 }}>
                ({format(row.byPlatform.IOS?.[field])} / {format(row.byPlatform.ANDROID?.[field])})
            </Box>
        </Box>
    )
}

function QualityWarningChip({ warnings }) {
    if (!Array.isArray(warnings) || warnings.length === 0) {
        return null
    }

    const warningMessages = warnings.map(formatQualityWarning)
    const visibleWarnings = warningMessages.slice(0, 5)
    const hiddenWarningCount = warningMessages.length - visibleWarnings.length

    return (
        <Tooltip
            arrow
            title={(
                <Stack spacing={0.5} sx={{ maxWidth: 420 }}>
                    {visibleWarnings.map((warning, index) => (
                        <Typography key={`${warning}-${index}`} variant="caption" component="div">
                            {warning}
                        </Typography>
                    ))}
                    {hiddenWarningCount > 0 && (
                        <Typography variant="caption" component="div">
                            외 {hiddenWarningCount}건
                        </Typography>
                    )}
                </Stack>
            )}
        >
            <Chip
                label={`품질 경고 ${warnings.length}`}
                color="warning"
                size="small"
                variant="outlined"
                aria-label={`품질 경고 ${warnings.length}건: ${warningMessages.join(' / ')}`}
            />
        </Tooltip>
    )
}

function SnsAuthTable({ label, rows }) {
    return (
        <ListTableContainer>
            <Table size="small" sx={{ minWidth: 820 }} aria-label={`${label} SNS 인증 현황`}>
                <TableHead>
                    <TableRow>
                        <TableCell>SNS 수단</TableCell>
                        <TableCell align="right">선택 사용자</TableCell>
                        <TableCell align="right">인증 성공률</TableCell>
                        <TableCell align="right">사용자 취소율</TableCell>
                        <TableCell align="right">오류율</TableCell>
                        <TableCell align="right">신규회원 판정 비율</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={`${row.marketType}-${row.method}`} hover>
                            <TableCell sx={{ fontWeight: 700 }}>
                                {formatSnsMethod(row.method)}
                            </TableCell>
                            <TableCell align="right">
                                {formatOptionalCount(row.selectedUsers)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatRate(row.successRate)}
                            </TableCell>
                            <TableCell align="right">{formatRate(row.cancelRate)}</TableCell>
                            <TableCell align="right">{formatRate(row.errorRate)}</TableCell>
                            <TableCell align="right">{formatRate(row.newAccountRate)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </ListTableContainer>
    )
}

export default function OnboardingFunnel({ role }) {
    const initialRangeRef = useRef(createInitialOnboardingRange())
    const requestSequenceRef = useRef(0)
    const stepRequestSequenceRef = useRef(0)
    const autoApplyInitializedRef = useRef(false)
    const stepFilterRef = useRef({
        flow: STEP_FLOW_OPTIONS[0].value,
        countryCode: 'ALL',
    })
    const stepSectionRef = useRef(null)
    const highlightedStepRef = useRef(null)
    const [from, setFrom] = useState(initialRangeRef.current.from)
    const [to, setTo] = useState(initialRangeRef.current.to)
    const [rangePreset, setRangePreset] = useState('28')
    const [platform, setPlatform] = useState('ALL')
    const [summary, setSummary] = useState(createEmptySummary)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(null)
    const [stepFlow, setStepFlow] = useState(STEP_FLOW_OPTIONS[0].value)
    const [stepCountryCode, setStepCountryCode] = useState('ALL')
    const [highlightedStepTarget, setHighlightedStepTarget] = useState(null)
    const [stepSummary, setStepSummary] = useState(createEmptyStepSummary)
    const [stepLoading, setStepLoading] = useState(true)
    const [stepLoadError, setStepLoadError] = useState(null)
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [entryExpanded, setEntryExpanded] = useState(false)
    const [cohortExpanded, setCohortExpanded] = useState(false)
    const [stepsExpanded, setStepsExpanded] = useState(false)
    const [visibleCountryCodes, setVisibleCountryCodes] = useState(readOnboardingCountryPreferences)

    stepFilterRef.current = {
        flow: stepFlow,
        countryCode: stepCountryCode,
    }

    const rows = useMemo(() => getCombinedEntryRows(summary), [summary])
    const stepRows = useMemo(() => sortOnboardingRowsNewestFirst(flattenStepRows(stepSummary)), [stepSummary])
    const cohortDays = useMemo(() => sortOnboardingRowsNewestFirst(stepSummary.days), [stepSummary])
    const entryAverageGroups = useMemo(() => [{
        key: platform,
        label: platform === 'ALL' ? '전체' : PLATFORM_OPTIONS.find(option => option.value === platform)?.label,
        rows,
    }], [platform, rows])
    const stepAverageGroups = useMemo(() => {
        const groups = new Map()
        stepRows.forEach(row => {
            const key = row.stepName
            if (!groups.has(key)) {
                groups.set(key, { key, label: row.displayName || row.stepName, order: row.order, rows: [] })
            }
            groups.get(key).rows.push(row)
        })
        return [...groups.values()].sort((left, right) => left.order - right.order)
    }, [stepRows])
    const highestDropByDate = useMemo(() => getHighestDropByDate(stepRows), [stepRows])
    const individualKpi = useMemo(
        () => getIndividualKpiForRange(summary),
        [summary]
    )
    const memberType = useMemo(
        () => getMemberTypeForRange(summary),
        [summary]
    )
    const flowComparisons = useMemo(
        () => getFlowComparisonsForRange(summary),
        [summary]
    )
    const flowComparisonByKey = useMemo(() => new Map(
        flowComparisons.map(comparison => [comparison.flowKey, comparison])
    ), [flowComparisons])
    const countryComparisons = useMemo(
        () => getCountryComparisonsForRange(summary),
        [summary]
    )
    const snsAuthRows = useMemo(
        () => getSnsAuthForRange(summary),
        [summary]
    )
    const selectedStepFlow = getStepFlow(stepFlow)
    const isGlobalStepFlow = selectedStepFlow.marketType === 'global'
    const countryFilterOptions = useMemo(() => {
        const countryComparisonByCode = new Map(
            countryComparisons.map(country => [country.countryCode, country])
        )

        return [{
            countryCode: 'ALL',
            cohortUsers: null,
            completedUsers: null,
            completionRate: null,
            detailAvailable: true,
        }, ...[...visibleCountryCodes, 'OTHER'].map((countryCode) => {
            const comparison = countryComparisonByCode.get(countryCode)

            return {
                countryCode,
                cohortUsers: comparison?.cohortUsers ?? null,
                completedUsers: comparison?.completedUsers ?? null,
                completionRate: comparison?.completionRate ?? null,
                detailAvailable: true,
            }
        })]
    }, [countryComparisons, visibleCountryCodes])
    const countryComparisonRows = useMemo(() => countryComparisons.map(country => ({
        ...country,
        detailAvailable: country.detailAvailable
            && ONBOARDING_COUNTRY_CODES.includes(country.countryCode),
    })), [countryComparisons])
    const selectedCountryStepRange = useMemo(
        () => getStepCohortForRange(stepSummary),
        [stepSummary]
    )
    const individualCohortUsers = individualKpi?.cohortUsers ?? null
    const individualCompletedUsers = individualKpi?.analyticsCompletedUsers ?? null
    const individualCompletionRate = individualKpi?.sameDayCompletionRate ?? null
    const individualHighestDrop = individualKpi?.highestDrop ?? null
    const warningCount = useMemo(() => (
        (summary.days || []).reduce((total, day) => (
            total + (Array.isArray(day.warnings) ? day.warnings.length : 0)
        ), 0)
    ), [summary.days])
    const stepWarningStats = useMemo(() => (
        (stepSummary.days || []).reduce((stats, day) => {
            const dayWarningCount = Array.isArray(day.warnings) ? day.warnings.length : 0

            return {
                warningCount: stats.warningCount + dayWarningCount,
                warningDayCount: stats.warningDayCount + (dayWarningCount > 0 ? 1 : 0),
            }
        }, { warningCount: 0, warningDayCount: 0 })
    ), [stepSummary.days])
    const selectedPlatformLabel = PLATFORM_OPTIONS.find(option => option.value === platform)?.label
        || platform
    const summaryRangeLabel = summary.meta?.from && summary.meta?.to
        ? `${summary.meta.from} ~ ${summary.meta.to}`
        : '-'
    const stepRangeLabel = stepSummary.meta?.from && stepSummary.meta?.to
        ? `${stepSummary.meta.from} ~ ${stepSummary.meta.to}`
        : summaryRangeLabel
    const individualCohortDescription = loading
        ? '조회 중'
        : loadError
            ? 'Summary 조회 실패'
            : individualCohortUsers === null
                ? '표시 가능한 일반회원 코호트 데이터가 없습니다.'
                : `${summaryRangeLabel} · ${selectedPlatformLabel} · 일별 일반회원 선택 완료 합계`
    const individualCompletedDescription = loading
        ? '조회 중'
        : loadError
            ? 'Summary 조회 실패'
            : individualCompletedUsers === null
                ? '표시 가능한 Analytics 가입 완료 데이터가 없습니다.'
                : `${summaryRangeLabel} · ${selectedPlatformLabel} · 일별 같은 날 sign_up 합계`
    const individualCompletionDescription = loading
        ? '조회 중'
        : loadError
            ? 'Summary 조회 실패'
            : individualCompletionRate === null
                ? '표시 가능한 당일 가입 완료율이 없습니다.'
                : `${summaryRangeLabel} · ${selectedPlatformLabel} · 완료 합계 ÷ 코호트 합계`
    const individualHighestDropDescription = loading
        ? '조회 중'
        : loadError
            ? 'Summary 조회 실패'
            : individualHighestDrop === null
                ? '표시 가능한 최고 이탈 구간이 없습니다.'
                : `${individualHighestDrop.date} · 조회 기간 중 최고 일별 ${formatHighestDropKind(individualHighestDrop.dropKind)} 이탈률 ${formatRate(individualHighestDrop.dropRate)}`
    const memberTypeDescription = loading
        ? '조회 중'
        : loadError
            ? 'Summary 조회 실패'
            : memberType === null
                ? '표시 가능한 회원 유형 분류 데이터가 없습니다.'
                : `${summaryRangeLabel} · ${selectedPlatformLabel} · 일별 최초 회원 유형 완료 합계`

    const showError = useCallback((message) => {
        setMessageSnackbar(message)
        setOpenSnackbar(true)
    }, [])

    const getData = useCallback(async (nextFrom, nextTo, nextPlatform) => {
        const validationMessage = validateOnboardingRange(nextFrom, nextTo)

        if (validationMessage) {
            showError(validationMessage)
            return
        }

        const requestId = requestSequenceRef.current + 1
        requestSequenceRef.current = requestId
        setLoading(true)
        setLoadError(null)

        try {
            const data = await getOnboardingFunnelSummary({
                from: nextFrom,
                to: nextTo,
                platform: nextPlatform,
            })

            if (requestId !== requestSequenceRef.current) {
                return
            }

            if (!data?.meta || !Array.isArray(data.days)) {
                throw new Error('온보딩 퍼널 응답 형식이 올바르지 않습니다.')
            }

            const nextSummary = {
                ...createEmptySummary(),
                ...data,
                meta: {
                    ...createEmptySummary().meta,
                    ...data.meta,
                    unavailableDates: Array.isArray(data.meta.unavailableDates)
                        ? data.meta.unavailableDates
                        : [],
                },
            }

            setSummary(nextSummary)
        } catch (error) {
            if (requestId !== requestSequenceRef.current) {
                return
            }

            const errorData = error.response?.data
            setSummary(createEmptySummary())

            if (errorData?.code === 'DATA_NOT_AVAILABLE') {
                setLoadError({
                    title: errorData.message || '제공 가능한 데이터가 없습니다.',
                    description: createUnavailableDescription(errorData.details),
                })
            } else {
                setLoadError({
                    title: '온보딩 퍼널 데이터를 조회하지 못했습니다.',
                    description: errorData?.message || error.message || '잠시 후 다시 조회해주세요.',
                })
            }
        } finally {
            if (requestId === requestSequenceRef.current) {
                setLoading(false)
            }
        }
    }, [showError])

    const getStepData = useCallback(async (
        nextFrom,
        nextTo,
        nextPlatform,
        nextFlowValue,
        nextCountryCode = 'ALL',
    ) => {
        const validationMessage = validateOnboardingRange(nextFrom, nextTo)

        if (validationMessage) {
            showError(validationMessage)
            return
        }

        const requestId = stepRequestSequenceRef.current + 1
        const flow = getStepFlow(nextFlowValue)
        stepRequestSequenceRef.current = requestId
        setStepLoading(true)
        setStepLoadError(null)

        try {
            const data = await getOnboardingFunnelSteps({
                from: nextFrom,
                to: nextTo,
                platform: nextPlatform,
                marketType: flow.marketType,
                authType: flow.authType,
                method: flow.method,
                countryCode: flow.marketType === 'global' ? nextCountryCode : 'ALL',
            })

            if (requestId !== stepRequestSequenceRef.current) {
                return
            }

            setStepSummary({
                ...createEmptyStepSummary(),
                ...data,
                meta: {
                    ...createEmptyStepSummary().meta,
                    ...data.meta,
                    unavailableDates: Array.isArray(data.meta.unavailableDates)
                        ? data.meta.unavailableDates
                        : [],
                },
                days: Array.isArray(data.days) ? data.days : [],
            })
        } catch (error) {
            if (requestId !== stepRequestSequenceRef.current) {
                return
            }

            const errorData = error.response?.data
            setStepSummary(createEmptyStepSummary())

            if (errorData?.code === 'DATA_NOT_AVAILABLE') {
                setStepLoadError({
                    title: errorData.message || '제공 가능한 가입 단계 데이터가 없습니다.',
                    description: createUnavailableDescription(errorData.details),
                })
            } else {
                setStepLoadError({
                    title: '가입 단계 데이터를 조회하지 못했습니다.',
                    description: errorData?.message || error.message || '잠시 후 다시 조회해주세요.',
                })
            }
        } finally {
            if (requestId === stepRequestSequenceRef.current) {
                setStepLoading(false)
            }
        }
    }, [showError])

    useEffect(() => {
        if (role >= 2) {
            getData(initialRangeRef.current.from, initialRangeRef.current.to, 'ALL')
            getStepData(
                initialRangeRef.current.from,
                initialRangeRef.current.to,
                'ALL',
                STEP_FLOW_OPTIONS[0].value,
            )
        }
    }, [getData, getStepData, role])

    useEffect(() => {
        if (role < 2) {
            return undefined
        }

        if (!autoApplyInitializedRef.current) {
            autoApplyInitializedRef.current = true
            return undefined
        }

        if (validateOnboardingRange(from, to)) {
            return undefined
        }

        const timer = setTimeout(() => {
            const { flow, countryCode } = stepFilterRef.current
            setHighlightedStepTarget(null)
            getData(from, to, platform)
            getStepData(from, to, platform, flow, countryCode)
        }, FILTER_AUTO_APPLY_DELAY_MS)

        return () => clearTimeout(timer)
    }, [from, getData, getStepData, platform, role, to])

    useEffect(() => {
        if (!stepLoading && highlightedStepTarget && highlightedStepRef.current) {
            highlightedStepRef.current.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        }
    }, [highlightedStepTarget, stepLoading, stepRows])

    const handleRangePresetChange = (event) => {
        const nextPreset = event.target.value
        setRangePreset(nextPreset)

        if (nextPreset === 'CUSTOM') {
            return
        }

        const nextRange = createOnboardingPresetRange(Number(nextPreset))
        setFrom(nextRange.from)
        setTo(nextRange.to)
    }

    const handleFromChange = (event) => {
        setFrom(event.target.value)
        setRangePreset('CUSTOM')
    }

    const handleToChange = (event) => {
        setTo(event.target.value)
        setRangePreset('CUSTOM')
    }

    const handleStepFlowChange = (_event, nextFlow) => {
        const nextFlowConfig = getStepFlow(nextFlow)
        const nextCountryCode = isGlobalStepFlow && nextFlowConfig.marketType === 'global'
            ? stepCountryCode
            : 'ALL'

        setStepFlow(nextFlow)
        setStepCountryCode(nextCountryCode)
        setHighlightedStepTarget(null)
        getStepData(from, to, platform, nextFlow, nextCountryCode)
    }

    const handleStepCountryChange = (nextCountryCode) => {
        if (!isGlobalStepFlow
            || (nextCountryCode !== 'ALL'
                && nextCountryCode !== 'OTHER'
                && !visibleCountryCodes.includes(nextCountryCode))) {
            return
        }

        setStepCountryCode(nextCountryCode)
        setHighlightedStepTarget(null)
        getStepData(from, to, platform, stepFlow, nextCountryCode)
    }

    const handleCountryDetail = (country) => {
        if (!country?.detailAvailable
            || !ONBOARDING_COUNTRY_CODES.includes(country.countryCode)) {
            return
        }

        const nextFlow = isGlobalStepFlow ? stepFlow : 'global_local'
        if (!visibleCountryCodes.includes(country.countryCode)) {
            const nextCountries = [...visibleCountryCodes, country.countryCode]
            setVisibleCountryCodes(nextCountries)
            saveOnboardingCountryPreferences(nextCountries)
        }
        setCohortExpanded(true)
        setStepFlow(nextFlow)
        setStepCountryCode(country.countryCode)
        setHighlightedStepTarget(null)
        getStepData(from, to, platform, nextFlow, country.countryCode)
        stepSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }

    const handleCountryRowKeyDown = (event, country) => {
        if (country.detailAvailable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault()
            handleCountryDetail(country)
        }
    }

    const handleVisibleCountriesSave = (countries) => {
        setVisibleCountryCodes(countries)
        if (!saveOnboardingCountryPreferences(countries)) {
            showError('국가 목록은 적용했지만 브라우저에 저장하지 못했습니다.')
        }
        if (stepCountryCode !== 'ALL' && stepCountryCode !== 'OTHER' && !countries.includes(stepCountryCode)) {
            setStepCountryCode('ALL')
            setHighlightedStepTarget(null)
            if (isGlobalStepFlow) getStepData(from, to, platform, stepFlow, 'ALL')
        }
    }

    const handleHighestDropDetail = () => {
        if (!individualHighestDrop) {
            return
        }

        const nextFlow = getStepFlow(individualHighestDrop.flowKey)
        setStepsExpanded(true)
        setStepFlow(nextFlow.value)
        setStepCountryCode('ALL')
        setHighlightedStepTarget({
            date: individualHighestDrop.date,
            stepName: individualHighestDrop.stepName,
            dropKind: individualHighestDrop.dropKind,
        })
        getStepData(from, to, platform, nextFlow.value, 'ALL')
        stepSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }

    if (role < 2) {
        return null
    }

    return (
        <>
            <Grid item xs={12} sx={{
                position: 'sticky', top: { xs: 56, sm: 64 },
                '@media (max-width: 599.95px) and (orientation: landscape)': { top: 48 },
                zIndex: theme => theme.zIndex.appBar - 1,
                alignSelf: 'flex-start',
            }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Title sx={{ mb: 0.5 }}>일반회원 온보딩 퍼널</Title>
                            <Typography variant="body2" color="text.secondary">
                                조회 기간 내 날짜별 KST 당일 코호트 · 고유 앱 설치 인스턴스/일 기준
                            </Typography>
                        </Box>
                        <ListFilterBar sx={{ width: 'auto', justifyContent: 'flex-end', mt: 0, mb: 0 }}>
                            <ListFilterForm onSubmit={event => event.preventDefault()}>
                                <TextField
                                    select
                                    label="조회 기간"
                                    size="small"
                                    value={rangePreset}
                                    onChange={handleRangePresetChange}
                                    sx={LIST_FILTER_SELECT_FIELD_SX}
                                >
                                    {RANGE_PRESET_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </TextField>
                                <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                                <TextField
                                    required
                                    label="시작 날짜"
                                    type="date"
                                    size="small"
                                    value={from}
                                    onChange={handleFromChange}
                                    inputProps={{ max: to }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={LIST_FILTER_DATE_FIELD_SX}
                                />
                                <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                                <TextField
                                    required
                                    label="종료 날짜"
                                    type="date"
                                    size="small"
                                    value={to}
                                    onChange={handleToChange}
                                    inputProps={{ min: from, max: initialRangeRef.current.to }}
                                    InputLabelProps={{ shrink: true }}
                                    sx={LIST_FILTER_DATE_FIELD_SX}
                                />
                                <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                                <TextField
                                    select
                                    label="OS"
                                    size="small"
                                    value={platform}
                                    onChange={event => setPlatform(event.target.value)}
                                    sx={LIST_FILTER_SELECT_FIELD_SX}
                                >
                                    {PLATFORM_OPTIONS.map(option => (
                                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                    ))}
                                </TextField>
                            </ListFilterForm>
                        </ListFilterBar>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            mt: 2,
                            mb: 2,
                            borderTop: '1px solid',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <MetadataItem label="조회 가능 시작일" value={summary.meta?.availableFrom} />
                        <MetadataItem label="Daily Export 완료일" value={summary.meta?.dataThrough} />
                        <MetadataItem label="최근 집계 갱신 (KST)" value={formatRefreshedAt(summary.meta?.refreshedAt)} />
                    </Box>

                    <Alert severity="info" variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                        <AlertTitle>지표 해석 안내</AlertTitle>
                        first_open은 다운로드가 아니라 설치 후 최초 실행입니다. 12.0은 일반회원과 기업회원을 모두 포함하며,
                        오늘 기준 2일 이내 데이터는 잠정치일 수 있습니다.
                    </Alert>

                    {warningCount > 0 && (
                        <Alert severity="warning" variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                            <AlertTitle>데이터 품질 경고 {warningCount}건</AlertTitle>
                            산술 검증에서 이상이 감지된 원본 수치를 보정하지 않고 표시합니다.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip label="12.0" color="primary" size="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            최초 실행 대비 당일 가입 시작률 (전체 · OS별 비교)
                        </Typography>
                    </Box>

                    {loading ? (
                        <Spiner />
                    ) : loadError ? (
                        <ListEmptyState
                            title={loadError.title}
                            description={loadError.description}
                            variant="error"
                        />
                    ) : rows.length === 0 ? (
                        <ListEmptyState
                            title="조회된 온보딩 진입 데이터가 없습니다."
                            description="조회 기간 또는 OS를 변경한 뒤 다시 확인해보세요."
                        />
                    ) : (
                        <>
                            <OnboardingPeriodAverage
                                label="OS별 조회기간 평균"
                                groupColumnLabel="OS"
                                rangeLabel={summaryRangeLabel}
                                groups={entryAverageGroups}
                                metrics={ENTRY_AVERAGE_METRICS}
                                showPlatformBreakdown
                            />
                            <DailyTableToggle
                                id="onboarding-entry-daily"
                                label="OS별 일별 데이터"
                                expanded={entryExpanded}
                                onClick={() => setEntryExpanded(value => !value)}
                            />
                            <Collapse id="onboarding-entry-daily" in={entryExpanded}>
                                <ListTableContainer>
                                    <Table aria-label="OS별 일별 데이터" size="small" sx={{ minWidth: 900 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>날짜 (KST)</TableCell>
                                                <TableCell align="right">신규 설치 후 최초 실행</TableCell>
                                                <TableCell align="right">당일 전체 가입 시작</TableCell>
                                                <TableCell align="right">미진입</TableCell>
                                                <TableCell align="right">당일 가입 시작률</TableCell>
                                                <TableCell>데이터 상태</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map(row => (
                                                <TableRow key={row.date} hover>
                                                    <TableCell>{row.date}</TableCell>
                                                    <TableCell align="right"><EntryMetricValue row={row} field="firstOpenUsers" /></TableCell>
                                                    <TableCell align="right"><EntryMetricValue row={row} field="signupStartedUsers" /></TableCell>
                                                    <TableCell align="right"><EntryMetricValue row={row} field="notStartedUsers" /></TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                        <EntryMetricValue row={row} field="signupStartRate" rate />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={0.75} alignItems="center">
                                                            <StatusChip
                                                                status={row.dataStatus}
                                                                config={DATA_STATUS_CONFIG}
                                                                fallbackLabel={row.dataStatus || '-'}
                                                            />
                                                            {row.warningCount > 0 && (
                                                                <Chip label={`품질 경고 ${row.warningCount}`} color="warning" size="small" variant="outlined" />
                                                            )}
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </ListTableContainer>
                            </Collapse>
                        </>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 1.5,
                            mb: 2,
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip label="12.1" color="primary" size="small" />
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                    일반회원 KPI 요약
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                상단 조회 기간에 포함된 KST 일별 코호트의 합산 지표입니다.
                            </Typography>
                        </Box>
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            aria-label="12.1 KPI 조회 기간 상태"
                        >
                            {individualKpi?.dataStatus && (
                                <StatusChip
                                    status={individualKpi.dataStatus}
                                    config={DATA_STATUS_CONFIG}
                                    fallbackLabel={individualKpi.dataStatus}
                                    aria-label="KPI 데이터 상태"
                                />
                            )}
                            <QualityWarningChip warnings={individualKpi?.warnings} />
                        </Stack>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                aria-label="일반회원 가입 퍼널 시작 KPI"
                                variant="outlined"
                                sx={{ p: 2, height: '100%', boxShadow: 'none' }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    일반회원 가입 퍼널 시작
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{ mt: 0.75, mb: 1, fontWeight: 800, color: 'primary.main' }}
                                >
                                    {loading ? '-' : formatOptionalCount(individualCohortUsers)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {individualCohortDescription}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                aria-label="Analytics 가입 완료 KPI"
                                variant="outlined"
                                sx={{ p: 2, height: '100%', boxShadow: 'none' }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Analytics 가입 완료
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{ mt: 0.75, mb: 1, fontWeight: 800, color: 'success.main' }}
                                >
                                    {loading ? '-' : formatOptionalCount(individualCompletedUsers)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {individualCompletedDescription}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                aria-label="당일 가입 완료율 KPI"
                                variant="outlined"
                                sx={{ p: 2, height: '100%', boxShadow: 'none' }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    당일 가입 완료율
                                </Typography>
                                <Typography
                                    variant="h4"
                                    sx={{ mt: 0.75, mb: 1, fontWeight: 800, color: 'info.main' }}
                                >
                                    {loading ? '-' : formatRate(individualCompletionRate)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {individualCompletionDescription}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper
                                variant="outlined"
                                sx={{
                                    height: '100%',
                                    boxShadow: 'none',
                                    borderColor: individualHighestDrop ? 'warning.main' : 'divider',
                                    overflow: 'hidden',
                                }}
                            >
                                <ButtonBase
                                    aria-label="최고 이탈 구간 KPI"
                                    disabled={loading || !individualHighestDrop}
                                    onClick={handleHighestDropDetail}
                                    sx={{
                                        display: 'block',
                                        width: '100%',
                                        height: '100%',
                                        p: 2,
                                        textAlign: 'left',
                                    }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        이탈률 최고 구간
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ mt: 0.75, mb: 1, fontWeight: 800, color: 'warning.dark' }}
                                    >
                                        {loading || !individualHighestDrop ? '-' : (
                                            <>
                                                <Box component="span" sx={{ display: 'block' }}>
                                                    {formatOnboardingFlow(individualHighestDrop.flowKey)}
                                                </Box>
                                                <Box component="span" sx={{ display: 'block' }}>
                                                    {formatHighestDropStep(individualHighestDrop.stepName)}
                                                </Box>
                                            </>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {individualHighestDropDescription}
                                    </Typography>
                                    {individualHighestDrop && (
                                        <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5 }}>
                                            클릭하여 해당 단계 보기
                                        </Typography>
                                    )}
                                </ButtonBase>
                            </Paper>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2.5 }} />
                    <Box aria-label="회원 유형 선택 현황">
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            회원 유형 선택 현황
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {memberTypeDescription}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    lg: 'repeat(5, minmax(0, 1fr))',
                                },
                                gap: 1.5,
                                mt: 1.25,
                            }}
                        >
                            {MEMBER_TYPE_METRICS.map(metric => (
                                <Paper
                                    key={metric.key}
                                    aria-label={`회원 유형 지표: ${metric.label}`}
                                    variant="outlined"
                                    sx={{ p: 1.5, boxShadow: 'none' }}
                                >
                                    <Typography variant="caption" color="text.secondary">
                                        {metric.label}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{ mt: 0.5, fontWeight: 800, color: metric.color }}
                                    >
                                        {loading
                                            ? '-'
                                            : metric.rate
                                                ? formatRate(memberType?.[metric.key])
                                                : formatOptionalCount(memberType?.[metric.key])}
                                    </Typography>
                                </Paper>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12} ref={stepSectionRef}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="12.2" color="primary" size="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            일반회원 가입 퍼널
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        가입 유형별 일반회원 선택 완료 코호트와 당일 Analytics 가입 완료를 비교합니다.
                    </Typography>

                    <Tabs
                        value={stepFlow}
                        onChange={handleStepFlowChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="가입 유형 선택"
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                        {STEP_FLOW_OPTIONS.map(option => (
                            <Tab
                                key={option.value}
                                value={option.value}
                                aria-label={option.label}
                                label={(
                                    <Stack component="span" direction="row" spacing={0.75} alignItems="center">
                                        <Box component="span">{option.label}</Box>
                                        <Chip
                                            component="span"
                                            label={formatRate(flowComparisonByKey.get(option.value)?.completionRate)}
                                            size="small"
                                            variant="outlined"
                                            sx={{ height: 20, fontWeight: 700 }}
                                        />
                                    </Stack>
                                )}
                            />
                        ))}
                    </Tabs>

                    {isGlobalStepFlow && (
                        <Box sx={{ mb: 2 }} aria-label="해외 가입 국가 필터">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    국가 필터
                                </Typography>
                                <OnboardingCountryFilterEditor countries={visibleCountryCodes} onSave={handleVisibleCountriesSave} />
                            </Box>
                            <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
                                {countryFilterOptions.map(country => (
                                    <Chip
                                        key={country.countryCode}
                                        label={country.countryCode === 'OTHER'
                                            ? '기타'
                                            : formatCountry(country.countryCode)}
                                        color={stepCountryCode === country.countryCode ? 'primary' : 'default'}
                                        variant={stepCountryCode === country.countryCode ? 'filled' : 'outlined'}
                                        onClick={country.detailAvailable
                                            ? () => handleStepCountryChange(country.countryCode)
                                            : undefined}
                                        disabled={!country.detailAvailable}
                                        aria-label={`가입 국가 필터: ${country.countryCode === 'OTHER'
                                            ? '기타'
                                            : formatCountry(country.countryCode)}`}
                                    />
                                ))}
                            </Stack>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                기타는 미국·일본·브라질·멕시코·파키스탄을 제외한 국가와 국가 미분류를 포함합니다.
                                태국·인도네시아 등 표시한 국가와 중복될 수 있습니다.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {stepLoading
                                    ? `${formatCountry(stepCountryCode)} 상세 조회 중`
                                    : selectedCountryStepRange
                                        ? `${formatCountry(stepCountryCode)} · ${stepRangeLabel} 합계 · 가입 시작 ${formatOptionalCount(selectedCountryStepRange.users)}명 → 가입 완료 ${formatOptionalCount(selectedCountryStepRange.analyticsCompletedUsers)}명 (완료율 ${formatRate(selectedCountryStepRange.sameDayCompletionRate)})`
                                        : `${formatCountry(stepCountryCode)} · ${stepRangeLabel} 조회 기간에 가입 퍼널 데이터가 없습니다.`}
                            </Typography>
                        </Box>
                    )}

                    {stepLoading ? (
                        <Spiner />
                    ) : stepLoadError ? (
                        <ListEmptyState
                            title={stepLoadError.title}
                            description={stepLoadError.description}
                            variant="error"
                        />
                    ) : stepSummary.days.length === 0 ? (
                        <ListEmptyState
                            title="조회된 일반회원 가입 코호트가 없습니다."
                            description="조회 기간, OS 또는 가입 유형을 변경한 뒤 다시 확인해보세요."
                        />
                    ) : (
                        <Stack spacing={2}>
                            {stepWarningStats.warningCount > 0 && (
                                <Alert severity="warning" variant="outlined" sx={{ boxShadow: 'none' }}>
                                    <AlertTitle>
                                        12.2 데이터 품질 경고 {stepWarningStats.warningCount}건
                                        {' '}({stepWarningStats.warningDayCount}일)
                                    </AlertTitle>
                                    산술 검증에서 이상이 감지된 원본 수치를 보정하지 않고 표시합니다.
                                    각 행의 품질 경고에 마우스를 올리면 세부 내용을 확인할 수 있습니다.
                                </Alert>
                            )}

                            <OnboardingPeriodAverage
                                label="일반회원 조회기간 평균"
                                groupColumnLabel="가입 유형"
                                rangeLabel={stepRangeLabel}
                                groups={[{
                                    key: stepFlow,
                                    label: `${selectedStepFlow.label}${isGlobalStepFlow ? ` · ${formatCountry(stepCountryCode)}` : ''}`,
                                    rows: cohortDays.map(day => day.cohort),
                                }]}
                                metrics={COHORT_AVERAGE_METRICS}
                            />
                            <Box>
                                <DailyTableToggle
                                    id="onboarding-cohort-daily"
                                    label="일반회원 일별 데이터"
                                    expanded={cohortExpanded}
                                    onClick={() => setCohortExpanded(value => !value)}
                                />
                                <Collapse id="onboarding-cohort-daily" in={cohortExpanded}>
                                    <ListTableContainer>
                                        <Table aria-label="일반회원 일별 데이터" size="small" sx={{ minWidth: 760 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>날짜 (KST)</TableCell>
                                                    <TableCell>코호트</TableCell>
                                                    <TableCell align="right">일반회원 선택 완료</TableCell>
                                                    <TableCell align="right">Analytics 가입 완료</TableCell>
                                                    <TableCell align="right">당일 가입 완료율</TableCell>
                                                    <TableCell>데이터 상태</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {cohortDays.map((day) => {
                                                    const cohort = day.cohort || {}

                                                    return (
                                                        <TableRow key={`${stepFlow}-${day.date}`} hover>
                                                            <TableCell>{day.date}</TableCell>
                                                            <TableCell sx={{ fontWeight: 700 }}>{cohort.name || '-'}</TableCell>
                                                            <TableCell align="right">{formatSafeNumber(cohort.users)}</TableCell>
                                                            <TableCell align="right">{formatSafeNumber(cohort.analyticsCompletedUsers)}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {formatRate(cohort.sameDayCompletionRate)}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                                    <StatusChip
                                                                        status={day.dataStatus}
                                                                        config={DATA_STATUS_CONFIG}
                                                                        fallbackLabel={day.dataStatus || '-'}
                                                                    />
                                                                    <QualityWarningChip warnings={day.warnings} />
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                            </TableBody>
                                        </Table>
                                    </ListTableContainer>
                                </Collapse>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    단계별 상세
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    각 단계 안에서의 이탈과 다음 단계로 넘어가지 않은 전환 이탈을 분리해 표시합니다.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    누적 도달률은 코호트 대비 단계 진입 사용자 비율이며, 평균 진입 횟수는 진입 이벤트 수를 진입 사용자 수로 나눈 값입니다.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    날짜별 내부 이탈률과 전환 이탈률 중 가장 높은 양수 구간을 강조합니다.
                                </Typography>
                            </Box>

                            {stepRows.length > 0 && (
                                <OnboardingPeriodAverage
                                    label="단계별 조회기간 평균"
                                    groupColumnLabel="단계"
                                    rangeLabel={stepRangeLabel}
                                    groups={stepAverageGroups}
                                    metrics={STEP_AVERAGE_METRICS}
                                />
                            )}
                            <Box>
                                <DailyTableToggle
                                    id="onboarding-steps-daily"
                                    label="단계별 일별 데이터"
                                    expanded={stepsExpanded}
                                    onClick={() => setStepsExpanded(value => !value)}
                                />
                                <Collapse id="onboarding-steps-daily" in={stepsExpanded}>
                                    {stepRows.length === 0 ? (
                                        <ListEmptyState
                                            title="조회된 단계 상세 데이터가 없습니다."
                                            description="선택한 가입 유형의 코호트는 있지만 단계별 집계가 비어 있습니다."
                                        />
                                    ) : (
                                        <ListTableContainer>
                                            <Table aria-label="단계별 일별 데이터" size="small" sx={{ minWidth: 1780 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>날짜 (KST)</TableCell>
                                                        <TableCell>단계</TableCell>
                                                        <TableCell align="right">진입 사용자</TableCell>
                                                        <TableCell align="right">완료 사용자</TableCell>
                                                        <TableCell align="right">단계 완료율</TableCell>
                                                        <TableCell align="right">내부 이탈 사용자</TableCell>
                                                        <TableCell align="right">내부 이탈률</TableCell>
                                                        <TableCell align="right">다음 단계 진입</TableCell>
                                                        <TableCell align="right">전환 이탈 사용자</TableCell>
                                                        <TableCell align="right">전환 이탈률</TableCell>
                                                        <TableCell align="right">누적 도달률</TableCell>
                                                        <TableCell align="right">진입 이벤트</TableCell>
                                                        <TableCell align="right">평균 진입 횟수</TableCell>
                                                        <TableCell>데이터 상태</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {stepRows.map((row) => {
                                                        const highestDrop = highestDropByDate[row.date]
                                                        const isHighestDropRow = highestDrop
                                                            && Number(highestDrop.order) === Number(row.order)
                                                            && highestDrop.stepName === row.stepName
                                                        const isHighestInternalDrop = isHighestDropRow
                                                            && highestDrop.dropKind === 'internal'
                                                        const isHighestTransitionDrop = isHighestDropRow
                                                            && highestDrop.dropKind === 'transition'
                                                        const isHighlightedStep = highlightedStepTarget
                                                            && highlightedStepTarget.date === row.date
                                                            && highlightedStepTarget.stepName === row.stepName

                                                        return (
                                                            <TableRow
                                                                key={`${stepFlow}-${row.date}-${row.order}-${row.stepName}`}
                                                                ref={isHighlightedStep ? highlightedStepRef : undefined}
                                                                hover
                                                                tabIndex={isHighlightedStep ? -1 : undefined}
                                                                aria-label={isHighlightedStep
                                                                    ? `선택한 최고 이탈 단계: ${row.date} ${row.displayName || row.stepName}`
                                                                    : undefined}
                                                                sx={isHighlightedStep
                                                                    ? { '& > td': { bgcolor: 'action.selected' } }
                                                                    : undefined}
                                                            >
                                                            <TableCell>{row.date}</TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                        {row.displayName || row.stepName || '-'}
                                                                    </Typography>
                                                                    {isHighestDropRow && (
                                                                        <Chip
                                                                            label={isHighestInternalDrop
                                                                                ? '최고 내부 이탈'
                                                                                : '최고 전환 이탈'}
                                                                            color="warning"
                                                                            size="small"
                                                                        />
                                                                    )}
                                                                </Stack>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    STEP {row.order || '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell align="right">{formatSafeNumber(row.entryUsers)}</TableCell>
                                                            <TableCell align="right">{formatSafeNumber(row.completeUsers)}</TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {formatRate(row.completionRate)}
                                                            </TableCell>
                                                            <TableCell align="right">{formatSafeNumber(row.internalDropUsers)}</TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={isHighestInternalDrop ? HIGHEST_DROP_CELL_SX : undefined}
                                                                aria-label={isHighestInternalDrop
                                                                    ? `날짜별 최고 내부 이탈률 ${formatRate(row.internalDropRate)}`
                                                                    : undefined}
                                                            >
                                                                {formatRate(row.internalDropRate)}
                                                            </TableCell>
                                                            <TableCell align="right">{formatSafeNumber(row.nextEntryUsers)}</TableCell>
                                                            <TableCell align="right">{formatSafeNumber(row.transitionDropUsers)}</TableCell>
                                                            <TableCell
                                                                align="right"
                                                                sx={isHighestTransitionDrop ? HIGHEST_DROP_CELL_SX : undefined}
                                                                aria-label={isHighestTransitionDrop
                                                                    ? `날짜별 최고 전환 이탈률 ${formatRate(row.transitionDropRate)}`
                                                                    : undefined}
                                                            >
                                                                {formatRate(row.transitionDropRate)}
                                                            </TableCell>
                                                            <TableCell align="right">{formatRate(row.cumulativeRate)}</TableCell>
                                                            <TableCell align="right">{formatOptionalCount(row.entryEventCount)}</TableCell>
                                                            <TableCell align="right">{formatEntryFrequency(row.reentryRate)}</TableCell>
                                                            <TableCell>
                                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                                    <StatusChip
                                                                        status={row.dataStatus}
                                                                        config={DATA_STATUS_CONFIG}
                                                                        fallbackLabel={row.dataStatus || '-'}
                                                                    />
                                                                    <QualityWarningChip warnings={row.warnings} />
                                                                </Stack>
                                                            </TableCell>
                                                            </TableRow>
                                                        )
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </ListTableContainer>
                                    )}
                                </Collapse>
                            </Box>
                        </Stack>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="12.3" color="primary" size="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            가입 유형별 비교
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {summaryRangeLabel} · {selectedPlatformLabel} · 일별 일반회원 선택 완료와 같은 날 Analytics 가입 완료 합계
                    </Typography>

                    {loading ? (
                        <Spiner />
                    ) : loadError ? (
                        <ListEmptyState
                            title="가입 유형별 비교 데이터를 조회하지 못했습니다."
                            description="상단의 Summary 조회 오류와 제공 가능 범위를 확인해주세요."
                            variant="error"
                        />
                    ) : flowComparisons.length === 0 ? (
                        <ListEmptyState
                            title="선택한 조회 기간의 가입 유형별 비교 데이터가 없습니다."
                            description="네 가입 유형의 집계가 준비된 날짜가 포함됐는지 확인해주세요."
                        />
                    ) : (
                        <Stack spacing={2} aria-label="가입 유형별 완료율 비교">
                            {flowComparisons.some(comparison => comparison.multiFlowWarning) && (
                                <Alert severity="info" variant="outlined" sx={{ boxShadow: 'none' }}>
                                    가입 유형별 사용자는 서로 중복될 수 있으므로 각 유형의 사용자 수를 전체 KPI로 합산하지 않습니다.
                                </Alert>
                            )}
                            {flowComparisons.map((comparison) => {
                                const rate = comparison.completionRate
                                const isAbnormalRate = rate !== null && (rate < 0 || rate > 1)
                                const isWeakRate = rate !== null && rate < 0.65
                                const barColor = rate === null
                                    ? 'action.disabled'
                                    : isAbnormalRate || isWeakRate
                                        ? 'warning.main'
                                        : 'primary.main'

                                return (
                                    <Box
                                        key={comparison.flowKey}
                                        aria-label={`가입 유형 비교: ${formatOnboardingFlow(comparison.flowKey)}`}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: {
                                                xs: '1fr',
                                                sm: '190px minmax(0, 1fr) 72px',
                                            },
                                            gap: 1.5,
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {formatOnboardingFlow(comparison.flowKey)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatOptionalCount(comparison.cohortUsers)} → {formatOptionalCount(comparison.completedUsers)}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                height: 10,
                                                borderRadius: 5,
                                                bgcolor: 'action.hover',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Box
                                                aria-label={`${formatOnboardingFlow(comparison.flowKey)} 완료율 막대 ${formatRate(rate)}`}
                                                sx={{
                                                    width: `${getFlowComparisonBarWidth(rate)}%`,
                                                    height: '100%',
                                                    borderRadius: 5,
                                                    bgcolor: barColor,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            align="right"
                                            sx={{
                                                fontWeight: 800,
                                                color: isAbnormalRate || isWeakRate
                                                    ? 'warning.dark'
                                                    : 'text.primary',
                                            }}
                                        >
                                            {formatRate(rate)}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Stack>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="12.3+" color="primary" size="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            해외 일반회원 국가 분류
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {summaryRangeLabel} · {selectedPlatformLabel} · 일별 해외 자체계정과 SNS 중복 제거 코호트 합계
                    </Typography>
                    <Alert severity="info" variant="outlined" sx={{ mb: 2, boxShadow: 'none' }}>
                        가입 라우팅과 서버 가입 요청에 사용된 국가 코드 기준입니다. 위치 기반 국가 확인이 불가능하면
                        기기 Region 값이 사용될 수 있으며, 유효한 국가 코드가 없으면 국가 미분류로 표시합니다.
                    </Alert>

                    {loading ? (
                        <Spiner />
                    ) : loadError ? (
                        <ListEmptyState
                            title="해외 국가 분류 데이터를 조회하지 못했습니다."
                            description="상단의 Summary 조회 오류와 제공 가능 범위를 확인해주세요."
                            variant="error"
                        />
                    ) : countryComparisons.length === 0 ? (
                        <ListEmptyState
                            title="선택한 조회 기간의 해외 국가 분류 데이터가 없습니다."
                            description="국가 코드가 포함된 해외 일반회원 코호트가 집계됐는지 확인해주세요."
                        />
                    ) : (
                        <ListTableContainer>
                            <Table size="small" sx={{ minWidth: 760 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>국가</TableCell>
                                        <TableCell>국가 코드</TableCell>
                                        <TableCell align="right">가입 시작</TableCell>
                                        <TableCell align="right">가입 완료</TableCell>
                                        <TableCell align="right">완료율</TableCell>
                                        <TableCell>단계 상세</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {countryComparisonRows.map(country => (
                                        <TableRow
                                            key={country.countryCode}
                                            hover={country.detailAvailable}
                                            onClick={country.detailAvailable
                                                ? () => handleCountryDetail(country)
                                                : undefined}
                                            onKeyDown={event => handleCountryRowKeyDown(event, country)}
                                            tabIndex={country.detailAvailable ? 0 : -1}
                                            aria-label={`국가별 가입 현황: ${formatCountry(country.countryCode)}`}
                                            aria-disabled={!country.detailAvailable}
                                            sx={{
                                                cursor: country.detailAvailable ? 'pointer' : 'default',
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 700 }}>
                                                {formatCountry(country.countryCode)}
                                            </TableCell>
                                            <TableCell>{country.countryCode}</TableCell>
                                            <TableCell align="right">{formatOptionalCount(country.cohortUsers)}</TableCell>
                                            <TableCell align="right">{formatOptionalCount(country.completedUsers)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                {formatRate(country.completionRate)}
                                            </TableCell>
                                            <TableCell>
                                                {country.detailAvailable
                                                    ? '클릭하여 12.2 단계 보기'
                                                    : '기타 필터에서 확인'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </ListTableContainer>
                    )}
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Chip label="12.4" color="primary" size="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            SNS 인증 현황
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {summaryRangeLabel} · {selectedPlatformLabel} · 일별 SNS 수단 선택과 인증 결과 합계
                    </Typography>

                    {loading ? (
                        <Spiner />
                    ) : loadError ? (
                        <ListEmptyState
                            title="SNS 인증 현황을 조회하지 못했습니다."
                            description="상단의 Summary 조회 오류와 제공 가능 범위를 확인해주세요."
                            variant="error"
                        />
                    ) : snsAuthRows.length === 0 ? (
                        <ListEmptyState
                            title="선택한 조회 기간의 SNS 인증 데이터가 없습니다."
                            description="국내 4개 및 해외 2개 SNS 수단의 집계가 준비된 날짜가 포함됐는지 확인해주세요."
                        />
                    ) : (
                        <Stack spacing={2.75}>
                            {SNS_MARKET_SECTIONS.map((section) => {
                                const rowsForMarket = snsAuthRows.filter(row => (
                                    row.marketType === section.marketType
                                ))

                                return (
                                    <Box key={section.marketType}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            flexWrap="wrap"
                                            sx={{ mb: 1 }}
                                        >
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                                {section.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {section.note}
                                            </Typography>
                                        </Stack>
                                        <SnsAuthTable label={section.label} rows={rowsForMarket} />
                                    </Box>
                                )
                            })}
                        </Stack>
                    )}
                </Paper>
            </Grid>
            {openSnackbar && (
                <Snackbars
                    open={openSnackbar}
                    severity="error"
                    message={messageSnackbar}
                    handleClose={() => setOpenSnackbar(false)}
                />
            )}
        </>
    )
}
